import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Calendar } from 'lucide-react';

const LOGO_URL = '/logo_transparent.png';

const POSTS = [
  {
    title: '5 Ways to Avoid Double Bookings Across Platforms',
    date: 'March 28, 2026',
    excerpt: 'Double bookings are the nightmare of every short-term rental host. When you list on Airbnb, Booking.com, and Vrbo simultaneously, calendar conflicts are almost inevitable — unless you have the right system in place.',
    content: `Double bookings are the nightmare of every short-term rental host. When you list on Airbnb, Booking.com, and Vrbo simultaneously, calendar conflicts are almost inevitable — unless you have the right system in place.

Here are five proven strategies to eliminate double bookings:

1. Use iCal Sync Across All Platforms
The iCal protocol (RFC 5545) allows calendars from different platforms to communicate. RentalHub syncs your Airbnb, Booking.com, and Vrbo calendars every 15 minutes, ensuring availability is always up to date.

2. Set Buffer Days Between Bookings
Adding a one-day buffer between check-out and the next check-in gives you breathing room for cleaning and prevents overlap caused by timezone differences or sync delays.

3. Centralise Your Calendar Management
Instead of logging into three different dashboards, use a single unified calendar. This gives you a bird's-eye view of all your bookings and makes conflicts immediately visible.

4. Enable Instant Notifications
Get real-time alerts when a new booking comes in from any platform. The faster you know about a booking, the faster you can block the dates on other platforms.

5. Automate Where Possible
Manual calendar management doesn't scale. Once you have more than two or three units, automation becomes essential. Tools like RentalHub handle the sync automatically so you can focus on guest experience.

The bottom line: prevention is always better than the awkward conversation with a guest whose booking you need to cancel.`,
  },
  {
    title: 'How to Price Your Short-Term Rental for Maximum Occupancy',
    date: 'March 15, 2026',
    excerpt: 'Pricing is the single biggest lever you have for controlling occupancy. Too high and your calendar stays empty. Too low and you leave money on the table. Here\'s how to find the sweet spot.',
    content: `Pricing is the single biggest lever you have for controlling occupancy. Too high and your calendar stays empty. Too low and you leave money on the table. Here's how to find the sweet spot.

Start With Your Costs
Before setting any price, calculate your true cost per night. Include mortgage or rent, utilities, cleaning fees, platform commissions (typically 3-15%), insurance, maintenance reserves, and consumables. This is your floor — you should rarely go below it.

Research Your Competition
Search for similar properties in your area on Airbnb and Booking.com. Note their nightly rates, minimum stays, and occupancy (you can estimate this from their review frequency). Position yourself competitively but don't race to the bottom.

Implement Dynamic Pricing
Adjust your rates based on demand. Weekends, holidays, and local events should command premium pricing. Midweek stays and low seasons may need discounts. RentalHub's analytics can help you identify these patterns from your booking data.

Use Minimum Stay Requirements Strategically
A 2-night minimum on weekends and a 3-night minimum during peak seasons can increase your average booking value while reducing turnover costs.

Monitor and Adjust Monthly
Review your occupancy rate and average nightly rate every month. If occupancy is above 85%, you may be underpriced. Below 60%, consider lowering rates or improving your listing.

The goal isn't to fill every night — it's to maximise total revenue while maintaining a sustainable workload.`,
  },
  {
    title: 'The Complete Guide to Guest Communication',
    date: 'February 28, 2026',
    excerpt: 'Great guest communication is the difference between a 4-star and a 5-star review. It starts before booking and continues well after checkout. Here\'s our complete framework.',
    content: `Great guest communication is the difference between a 4-star and a 5-star review. It starts before booking and continues well after checkout. Here's our complete framework.

Before Booking: Respond Fast
Response time is one of the biggest factors in converting enquiries to bookings. Platforms like Airbnb even track and display your response rate. Aim to reply within 1 hour during business hours. AI-powered message suggestions can help you craft professional replies in seconds.

After Booking: Set Expectations
Send a confirmation message that includes check-in instructions, parking details, WiFi password, and house rules. The more information you provide upfront, the fewer questions you'll get later.

Day Before Check-in: Personal Touch
A brief message the day before arrival — "Looking forward to hosting you! Let me know if you need anything" — goes a long way in making guests feel welcome.

During the Stay: Be Available, Not Intrusive
Let guests know you're available if needed, but don't over-message. A single check-in message on the first morning is sufficient: "Hope you settled in well. Don't hesitate to reach out if you need anything."

After Checkout: Thank and Request Review
Thank the guest for staying and politely ask for a review. This is also a good time to mention that you'd love to host them again and offer a direct booking discount for return visits.

Handling Complaints
Respond to complaints quickly and empathetically. Acknowledge the issue, apologise, and offer a concrete solution. Even if the complaint seems unreasonable, a professional response can prevent a negative review.

Using Templates
Create message templates for each stage of the guest journey. This ensures consistency and saves time, especially when managing multiple properties. RentalHub's messaging feature lets you store and quickly send templated responses.`,
  },
];

export default function Blog() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white" data-testid="blog-page">
      <header className="border-b border-slate-200 py-4 px-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="relative cursor-pointer" onClick={() => navigate('/')}>
            <img src={LOGO_URL} alt="RentalHub" className="h-10 object-contain" />
            <span className="absolute text-[4px] font-semibold text-slate-400 select-none" style={{ top: '28%', right: '12%' }}>TM</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/')} data-testid="blog-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1.5" />Back to Home
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-16">
        <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mb-2" data-testid="blog-heading">Blog</h1>
        <p className="text-base text-slate-500 mb-12">Tips, guides, and insights for short-term rental hosts</p>

        <div className="space-y-10">
          {POSTS.map((post, i) => (
            <article key={i} className="border border-slate-200 rounded-xl p-6 sm:p-8 hover:shadow-md transition-shadow" data-testid={`blog-post-${i}`}>
              <div className="flex items-center gap-2 text-xs text-slate-400 mb-3">
                <Calendar className="w-3.5 h-3.5" />{post.date}
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-3">{post.title}</h2>
              <p className="text-sm text-slate-600 leading-relaxed mb-4">{post.excerpt}</p>
              <details className="group">
                <summary className="text-sm font-semibold text-slate-700 cursor-pointer hover:text-slate-900 select-none" data-testid={`blog-read-more-${i}`}>
                  Read more
                </summary>
                <div className="mt-4 text-sm text-slate-600 leading-relaxed whitespace-pre-line">{post.content}</div>
              </details>
            </article>
          ))}
        </div>
      </main>

      <footer className="py-8 px-4 border-t border-slate-200 text-center">
        <p className="text-xs text-slate-400">&copy; {new Date().getFullYear()} RentalHub. All rights reserved.</p>
      </footer>
    </div>
  );
}
